import type { AppointmentStatus } from "./domain";
import { avoidSunday, parseZonedDateTimeInput, startOfNextZonedDay } from "./zoned-time";

/** How far ahead the AI may put an appointment (ADR 0059). */
export const APPOINTMENT_AHEAD_MAX_DAYS = 366;

type TimeSlot = { startsAt: Date; endsAt: Date };

export function appointmentEndsAt(startsAt: Date, durationMinutes: number): Date {
  return new Date(startsAt.getTime() + durationMinutes * 60_000);
}

/** The start of an AI proposal (`YYYY-MM-DDTHH:mm` in the workspace zone); null if invalid. */
export function appointmentStartFromLocal(value: string, timeZone: string): Date | null {
  return parseZonedDateTimeInput(value, timeZone);
}

/** A start the AI may propose and an operator may approve: in the future, within a year. */
export function isAppointmentStartAllowed(startsAt: Date, now: Date): boolean {
  return (
    startsAt.getTime() > now.getTime() &&
    startsAt.getTime() <= now.getTime() + APPOINTMENT_AHEAD_MAX_DAYS * 86_400_000
  );
}

export function appointmentsOverlap(left: TimeSlot, right: TimeSlot): boolean {
  return left.startsAt < right.endsAt && right.startsAt < left.endsAt;
}

/** Scheduled appointments of `others` overlapping `slot`, except `excludeId` (the one being moved). */
export function appointmentConflicts<
  T extends TimeSlot & { id: string; status: AppointmentStatus },
>(slot: TimeSlot, others: readonly T[], excludeId?: string | null): T[] {
  return others.filter(
    (other) =>
      other.id !== excludeId && other.status === "scheduled" && appointmentsOverlap(slot, other),
  );
}

/** When the reminder is due; null without reminder. */
export function appointmentReminderAt(appointment: {
  startsAt: Date;
  reminderMinutes: number | null;
}): Date | null {
  if (appointment.reminderMinutes === null) return null;
  return new Date(appointment.startsAt.getTime() - appointment.reminderMinutes * 60_000);
}

/**
 * Rule follow-ups of a lead with a scheduled appointment are not due before
 * the day after it, Sunday moved to Monday (ADR 0059). Null without one.
 */
export function followupsNotBefore(
  appointments: ReadonlyArray<TimeSlot & { status: AppointmentStatus }>,
  timeZone: string,
): Date | null {
  const last = appointments
    .filter((appointment) => appointment.status === "scheduled")
    .map((appointment) => appointment.endsAt)
    .sort((left, right) => right.getTime() - left.getTime())[0];
  return last ? avoidSunday(startOfNextZonedDay(last, timeZone), timeZone) : null;
}
